import { readSocialCallback } from '@/entities/auth/social/socialRedirect';
import { rememberLastProvider } from '@/pages/login/components/SocialLoginButtons';
import { getApiErrorCode } from '@/shared/api/http-client';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { useAuthStore } from '@/stores/authStore';
import { getRouteApi } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

const route = getRouteApi('/auth/callback/$provider');

/**
 * M-01 SNS 로그인 콜백 — 제공자에서 돌아오면 서버에 로그인을 요청하고 바로 떠난다.
 * 실패하면 로그인 화면으로 돌아가 X1(인증 실패) 또는 X4(가입 이력 없음)를 띄운다.
 */
export const SocialCallbackPage = () => {
  const { provider } = route.useParams();
  const navigate = route.useNavigate();
  const socialLogin = useAuthStore((s) => s.socialLogin);
  // 저장한 state를 한 번만 쓰므로 StrictMode의 두 번째 실행을 막는다
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const fail = (
      reason: 'social_failed' | 'signup_required',
      returnTo?: string,
    ) =>
      navigate({
        to: ROUTE_PATHS.LOGIN,
        search: { reason, provider, redirect: returnTo },
        replace: true,
      });

    const run = async () => {
      const result = await readSocialCallback(provider);
      if (!result.ok) {
        void fail('social_failed', result.returnTo);
        return;
      }
      await socialLogin(provider, result.body)
        .then(() => {
          rememberLastProvider(provider);
          // 인가 코드 · 토큰이 담긴 콜백 주소는 기록에 남기지 않는다
          if (result.returnTo) {
            void navigate({ href: result.returnTo, replace: true });
          } else {
            void navigate({ to: ROUTE_PATHS.ACCOUNTS, replace: true });
          }
        })
        .catch((error: unknown) => {
          void fail(
            getApiErrorCode(error) === 'WEB_SIGNUP_NOT_SUPPORTED'
              ? 'signup_required'
              : 'social_failed',
            result.returnTo,
          );
        });
    };
    void run();
  }, [provider, navigate, socialLogin]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface">
      <Loader2
        className="size-6 animate-spin text-ink-6"
        aria-label="로그인 중"
      />
    </div>
  );
};
